<template>
    <div class="full-height">
        <div class="row justify-end items-center bg-primary q-px-md q-py-sm">
            <div>
                <q-btn-group flat v-if="!$q.platform.is.mobile">
                    <q-btn
                        id="download-csv-button"
                        @click="exportDataAsCSV"
                        :loading="isDownloadingCSV"
                        class="download-file-buttons"
                        icon="far fa-file-csv"
                        text-color="white"
                        size="12px"
                        flat
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Export raw data (CSV)') }}
                            <br />
                            {{
                                $t(
                                    'Filtered elements will not be present on the downloadable report.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        id="download-xls-button"
                        @click="exportDataAsXLS"
                        :loading="isDownloadingXLS"
                        text-color="white"
                        class="download-file-buttons"
                        icon="far fa-file-excel"
                        size="12px"
                        flat
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Export raw data (XLS)') }}
                            <br />
                            {{
                                $t(
                                    'Filtered elements will not be present on the downloadable report.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                </q-btn-group>
            </div>
        </div>

        <!-- Table mode -->
        <maintenance-report-table
            class="q-mb-md"
            :report="report"
            :label="
                $t('Current Period: {startDate} - {endDate}', {
                    startDate: this.formatDate(report.dateInterval.start),
                    endDate: this.formatDate(report.dateInterval.end)
                })
            "
        />
        <maintenance-report-table
            :report="comparisonReport"
            :label="
                $t('Comparison Period: {startDate} - {endDate}', {
                    startDate: this.formatDate(
                        comparisonReport.dateInterval.start
                    ),
                    endDate: this.formatDate(comparisonReport.dateInterval.end)
                })
            "
        />
    </div>
</template>

<script>
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import { mapGetters } from 'vuex';
import MaintenanceReportTable from './MaintenanceReportTable.vue';
import { buildTable } from './maintenanceReport.js';
import { mapState } from 'pinia';
import { useReportsStore } from '@stores/reports.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'ComparisonMaintenanceReport',
    components: { MaintenanceReportTable },
    props: {
        records: {
            type: Object,
            required: true
        },
        facilityId: {
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        }
    },
    created() {
        // initialize file exporter instance
        this.fileExporter = new FileExporter({
            headers: this.fileHeaders
        });
    },
    data() {
        return {
            // button loading animation
            isDownloadingCSV: false,
            isDownloadingXLS: false,

            // file exporter class
            fileExporter: null
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset',
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get the file headers for file exporting
         * @return {Array} file headers for file exporting
         */
        fileHeaders() {
            return [
                // match the columns in the fileRows
                this.$t('Facility'),
                this.$t('Asset'),
                this.$t('Cause'),
                this.$t('Cause code'),
                this.$t('Equipment failure'),
                this.$t('Running (min)'),
                this.$t('Total downtime (min)'),
                this.$t('Equipment failure duration (min)'),
                this.$t('Number of downtimes'),
                this.$t('Number of equipment failures'),
                this.$t('MTTR (min)'),
                this.$t('MTBF (min)'),
                this.$t('Report Start Date').concat(` [${this.localTimezone}]`),
                this.$t('Report End Date').concat(` [${this.localTimezone}]`)
            ];
        },
        /**
         * Build report data of current interval
         * @return {Object} report data containing table data and date interval
         */
        report() {
            // assign date intervals to corresponding record data
            const { currentReportData } = this.records;
            const report = {
                ...this.generateReport(currentReportData),
                dateInterval: {
                    start: this.getDateRange.start,
                    end: this.getDateRange.end
                }
            };
            return report;
        },
        /**
         * Build report data of comparison interval
         * @return {Object} report data of comparison interval
         */
        comparisonReport() {
            // assign date intervals to corresponding record data
            const { comparisonReportData } = this.records;
            const report = {
                ...this.generateReport(comparisonReportData),
                dateInterval: {
                    start: this.getDateRange.compareStart,
                    end: this.getDateRange.compareEnd
                }
            };
            return report;
        },
        /**
         * All possible filters for this report
         * @return {Object} all possible filters for this report
         */
        filters() {
            return {
                assetIds: this.selectedAssets
            };
        },
        /**
         * Extract facility name from facility id
         * @return {String} facility name
         */
        facilityName() {
            return this.getFacility(this.facilityId).name.toLowerCase();
        },
        /**
         * Get local timezone
         * @return {String} local timezone
         */
        localTimezone() {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                return timezone;
            }
            return 'UTC';
        },
        /**
         * Get the file rows for file exporting
         * @return {Array} file rows for exporting
         */
        fileRows() {
            const { currentReportData, comparisonReportData } = this.records;

            const currentReportRows = this.getFileRows(
                currentReportData,
                this.getDateRange.start,
                this.getDateRange.end
            );
            const comparisonReportRows = this.getFileRows(
                comparisonReportData,
                this.getDateRange.compareStart,
                this.getDateRange.compareEnd
            );

            return [...currentReportRows, ...comparisonReportRows];
        }
    },
    methods: {
        /**
         * Get the file rows for file exporting for a report period
         * @param {Array} records - Records to process
         * @param {Date|String} start - Start date of the report period
         * @param {Date|String} end - End date of the report period
         * @returns {Array} An array of formatted rows with maintenance data and calculated asset totals
         */
        getFileRows(records, start, end) {
            // Filter records by selected assets
            const filteredRecords = records.filter(record =>
                this.filters.assetIds.has(record['asset_id'])
            );

            const rows = [];
            const assetTotalsMap = new Map();

            // Process all records in a single loop
            for (const record of filteredRecords) {
                const assetId = record['asset_id'];
                const asset = this.getAsset(assetId);
                const isDeleted = asset ? asset.is_deleted || false : false;
                const facilityName = this.getFacility(
                    record['facility_id']
                ).name;
                const assetName = !isDeleted
                    ? record['asset_name']
                    : this.$t('{assetName} (Deleted)', {
                          assetName: record['asset_name']
                      });

                // Create row for individual record
                rows.push([
                    facilityName,
                    assetName,
                    record['cause_name'],
                    record['cause_code'],
                    record['equipment_failure'],
                    record['running_duration_min'],
                    record['downtime_duration_min'],
                    record['equipment_failure_duration_min'],
                    record['number_downtimes'],
                    record['number_equipment_failures'],
                    record['mttr_min'],
                    record['mtbf_min'],
                    this.formatDate(start),
                    this.formatDate(end)
                ]);

                // Initialize or update asset totals
                if (!assetTotalsMap.has(assetId)) {
                    assetTotalsMap.set(assetId, {
                        facilityName,
                        assetName,
                        running_duration_min:
                            record['running_duration_min'] || 0,
                        downtime_duration_min:
                            record['downtime_duration_min'] || 0,
                        equipment_failure_duration_min:
                            record['equipment_failure_duration_min'] || 0,
                        number_downtimes: record['number_downtimes'] || 0,
                        number_equipment_failures:
                            record['number_equipment_failures'] || 0
                    });
                } else {
                    const totals = assetTotalsMap.get(assetId);
                    // We don't accumulate running_duration_min as it should be counted once per asset
                    totals.downtime_duration_min +=
                        record['downtime_duration_min'] || 0;
                    totals.equipment_failure_duration_min +=
                        record['equipment_failure_duration_min'] || 0;
                    totals.number_downtimes += record['number_downtimes'] || 0;
                    totals.number_equipment_failures +=
                        record['number_equipment_failures'] || 0;
                }
            }

            // Add total rows for each asset
            for (const totals of assetTotalsMap.values()) {
                // Calculate MTTR and MTBF
                const mttr =
                    totals.number_equipment_failures > 0
                        ? totals.equipment_failure_duration_min /
                          totals.number_equipment_failures
                        : 0;

                const mtbf =
                    totals.number_equipment_failures > 0
                        ? totals.running_duration_min /
                          totals.number_equipment_failures
                        : 0;

                // Add the totals row
                rows.push([
                    totals.facilityName,
                    totals.assetName,
                    this.$t('Asset total'), // Indicate this is a total row
                    '', // No cause code for totals
                    '', // No equipment failure for totals
                    totals.running_duration_min,
                    totals.downtime_duration_min,
                    totals.equipment_failure_duration_min,
                    totals.number_downtimes,
                    totals.number_equipment_failures,
                    mttr,
                    mtbf,
                    this.formatDate(start),
                    this.formatDate(end)
                ]);
            }

            return rows;
        },
        /**
         * Generate table data from report period
         * @param {Object} periodData
         * @return {Object} table data for specified period
         */
        generateReport(periodData) {
            const tableData = buildTable(periodData, this.filters);
            // // sort the rows for the array.
            tableData.rows.sort(function (a, b) {
                let firstAssetName = a.asset_name.toLowerCase();
                let secondAssetName = b.asset_name.toLowerCase();
                return firstAssetName.localeCompare(secondAssetName);
            });
            return tableData;
        },
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Export raw records as CSV.
         */
        exportDataAsCSV() {
            // begin loading
            this.isDownloadingCSV = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                this.fileExporter.exportCSV({
                    filename: `${this.facilityName}_maintenance_${start_date}_${end_date}`,
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('CSV');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingCSV = false;
        },
        /**
         * Export raw records as XLS.
         */
        exportDataAsXLS() {
            // begin loading
            this.isDownloadingXLS = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                this.fileExporter.exportXLS({
                    filename: `${this.facilityName}_maintenance_${start_date}_${end_date}`,
                    sheetName: 'Performance Report',
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('XLS');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingXLS = false;
        }
    }
};
</script>

<style lang="scss" scoped></style>
